import './SelectMenu.css'

import type { Placement } from '@floating-ui/react-dom'
import fuzzaldrin from 'fuzzaldrin-plus'
import { ReactNode, useCallback, useMemo, useState } from 'react'

import { useFocus } from '../hooks/useFocus'
import { cx } from '../utilities/cx'
import { RefOrEl } from '../utilities/normalize-to-el'
import { Icon, IconName } from './Icon'
import { Menu } from './Menu'
import { ScrollArea } from './ScrollArea'
import { TextField } from './TextField'

export type MenuItemKind = 'default' | 'destructive'

export type SelectMenuItem<V extends string = string> = {
  value?: V
  disabled?: boolean
  text?: string
  searchText?: string
  icon?: IconName
  startIcon?: IconName
  endIcon?: IconName
  kind?: MenuItemKind
  content?: ReactNode
  onClick?: () => void
}

export type SelectMenuItemGroup<V extends string = string> = {
  name?: string
  items: SelectMenuItem<V>[]
}

export type SelectMenuProps = {
  className?: string
  targetRefOrEl?: RefOrEl<HTMLDivElement>
  targetPosition?: { x: number; y: number }
  target?: ReactNode
  placement?: Placement
  items?: SelectMenuItem[] | SelectMenuItemGroup[]
  value?: string
  tabIndex?: number
  disabled?: boolean
  searchable?: boolean
  onChange?: (value: string) => void
  onFocus?: () => void
  onBlur?: () => void
  onOpen?: () => void
  onClose?: () => void
}

export const SelectMenu = (props: SelectMenuProps) => {
  const {
    className,
    targetRefOrEl,
    targetPosition,
    target,
    placement = 'top',
    items: itemsOrItemGroups = [],
    value,
    tabIndex,
    disabled,
    searchable,
    onChange,
    onFocus,
    onBlur,
    onOpen,
    onClose,
  } = props

  const focus = useFocus()

  const [isOpen, setIsOpen] = useState(false)
  const [shouldClose, setShouldClose] = useState(false)
  const [searchValue, setSearchValue] = useState('')

  const itemGroups = useMemo(
    () =>
      itemsOrItemGroups.length !== 0 && 'items' in itemsOrItemGroups[0]!
        ? (itemsOrItemGroups as SelectMenuItemGroup[])
        : ([{ items: itemsOrItemGroups }] as SelectMenuItemGroup[]),
    [itemsOrItemGroups],
  )

  const items = useMemo(() => itemGroups.flatMap((g) => g.items), [itemGroups])

  const handleOpen = () => {
    setIsOpen(true)
    onOpen?.()
  }

  const handleClose = () => {
    setIsOpen(false)
    setShouldClose(false)
    setSearchValue('')
    onClose?.()
  }

  const handleLayerSelect = useCallback(
    (element: HTMLElement) => {
      const itemValue = element.dataset['value']
      if (itemValue === undefined) return
      const item = items.find((i) => i.value === itemValue)
      if (item?.disabled) return
      if (onChange) onChange(itemValue)
      item?.onClick?.()
      setShouldClose(true)
    },
    [items, onChange],
  )

  const [SelectLayer, selectFocus] = focus.layer(isOpen, {
    navigation: 'menu',
    onSelect: handleLayerSelect,
    onEscape: () => setShouldClose(true),
  })

  let hasAutoFocused = false

  const handleTargetRef = focus.ref({ onSelect: handleOpen })

  const isSearching = searchable && !!searchValue

  const searchItems = useMemo(
    () =>
      fuzzaldrin
        .filter(
          items.map((i) => ({ item: i, term: i.text ?? i.searchText })),
          searchValue,
          { key: 'term' },
        )
        .map((i) => {
          const displayText = i.item.text ?? i.item.searchText
          return {
            ...i.item,
            textHtml: displayText
              ? fuzzaldrin.wrap(displayText, searchValue, {
                  wrap: { tagOpen: '<span>', tagClose: '</span>' },
                })
              : '',
          }
        }),
    [items, searchValue],
  )

  const handleItemSelect = (item: SelectMenuItem) => () => {
    if (item.disabled) return
    if ('value' in item && onChange) onChange(item.value as any)
    item.onClick?.()
    setShouldClose(true)
  }

  const renderItem = (item: SelectMenuItem & { textHtml?: string }, index: number) => {
    const startIcon = item.startIcon ?? item.icon
    const isSelected = 'value' in item && item.value === value
    const kind = item.kind ?? 'default'
    const isDisabled = !!item.disabled
    const shouldAutoFocus = !isDisabled && !hasAutoFocused
    if (shouldAutoFocus) hasAutoFocused = true

    const menuItemContent = item.textHtml ? (
      <div
        className="select-menu__item-content"
        dangerouslySetInnerHTML={{
          __html: item.textHtml.replace(/ /g, '&nbsp;'),
        }}
      />
    ) : (
      <div className="select-menu__item-content">
        {item.text}
        {item.content}
      </div>
    )

    const itemClasses = cx('select-menu__item', {
      'select-menu__item--selected': isSelected,
      'select-menu__item--destructive': kind === 'destructive',
      'select-menu__item--disabled': isDisabled,
    })

    return (
      <div
        key={index}
        className={itemClasses}
        tabIndex={-1}
        data-value={item.value}
        ref={selectFocus.ref({ disabled: isDisabled, autoFocus: shouldAutoFocus })}
        onClick={handleItemSelect(item)}
      >
        {startIcon && (
          <div className="select-menu__item-icon">
            <Icon name={startIcon} />
          </div>
        )}
        {(item.textHtml ?? item.text ?? item.content) && menuItemContent}
        {item.endIcon && (
          <div className="select-menu__item-icon">
            <Icon name={item.endIcon} />
          </div>
        )}
      </div>
    )
  }

  const renderItemGroup = (itemGroup: SelectMenuItemGroup, index: number) => (
    <div key={index} className="select-menu__item-group">
      {!!itemGroup?.name && <div className="select-menu__item-group-name">{itemGroup.name}</div>}
      {itemGroup.items.map((item, i) => renderItem(item, i))}
    </div>
  )

  return (
    <Menu
      className={className}
      targetRefOrEl={targetRefOrEl}
      targetPosition={targetPosition}
      target={target}
      tabIndex={tabIndex}
      disabled={disabled}
      placement={placement}
      shouldClose={shouldClose}
      shouldOpen={isOpen}
      targetRef={handleTargetRef}
      onFocus={onFocus}
      onBlur={onBlur}
      onOpen={handleOpen}
      onClose={handleClose}
    >
      <SelectLayer>
        {searchable && (
          <div className="select-menu__search">
            <TextField
              icon="search"
              size="small"
              placeholder="Search"
              value={searchValue}
              onChange={setSearchValue}
            />
          </div>
        )}
        <ScrollArea>
          {isSearching
            ? searchItems.map((item, i) => renderItem(item, i))
            : itemGroups.map((itemGroup, i) => renderItemGroup(itemGroup, i))}
        </ScrollArea>
      </SelectLayer>
    </Menu>
  )
}
